import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import { getUnit, updateUnit } from '@/lib/services/units'

// ─── Atividades (replicadas do preco-certo/use-profile) ──────────────────────
export type ActivityId = 'alimentos' | 'artesanato' | 'metalurgica' | 'construcao' | 'manicure' | 'agro'

export interface Activity {
  id: ActivityId
  name: string
  icon: string
  categories: string[]
}

export const ACTIVITIES: Activity[] = [
  {
    id: 'alimentos',
    name: 'Alimentos',
    icon: '🍰',
    categories: ['Bolos e Tortas', 'Salgados', 'Doces', 'Bebidas', 'Conservas', 'Outros'],
  },
  {
    id: 'artesanato',
    name: 'Artesanato',
    icon: '🎨',
    categories: ['Bijuterias', 'Decoração', 'Costura', 'Crochê', 'Pintura', 'Outros'],
  },
  {
    id: 'metalurgica',
    name: 'Metalúrgica',
    icon: '⚙️',
    categories: ['Ferramentas', 'Peças', 'Estruturas', 'Máquinas', 'Componentes', 'Outros'],
  },
  {
    id: 'construcao',
    name: 'Construção',
    icon: '🏗️',
    categories: ['Móveis', 'Esquadrias', 'Estruturas', 'Acabamentos', 'Instalações', 'Outros'],
  },
  {
    id: 'manicure',
    name: 'Manicure / Pedicure',
    icon: '💅',
    categories: ['Esmaltação', 'Alongamento', 'Unhas de Gel', 'Spa dos Pés', 'Tratamentos', 'Outros'],
  },
  {
    id: 'agro',
    name: 'Agro / Rural',
    icon: '🌾',
    categories: ['Rações e Suplementos', 'Grãos e Sementes', 'Horticultura', 'Fruticultura', 'Laticínios', 'Outros'],
  },
]

// ─────────────────────────────────────────────────────────────────────────────

interface ActivityUpdateModalProps {
  unitId: string
  isOpen: boolean
  onClose: () => void
  onUpdated?: () => void
}

export function ActivityUpdateModal({ unitId, isOpen, onClose, onUpdated }: ActivityUpdateModalProps) {
  const { t } = useTranslation()
  const [selectedActivity, setSelectedActivity] = useState<ActivityId | null>(null)
  const [currentAttrs, setCurrentAttrs] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen && unitId) {
      loadCurrentActivity()
    }
  }, [isOpen, unitId])

  const loadCurrentActivity = async () => {
    setIsLoading(true)
    try {
      const unit = await getUnit(unitId)
      const attrs = (unit as any).attrs ?? {}
      setCurrentAttrs(attrs)
      setSelectedActivity((attrs.activity as ActivityId) ?? null)
    } catch (error) {
      console.error('[ActivityUpdateModal] Error loading unit:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!selectedActivity) return
    setIsSaving(true)
    try {
      await updateUnit(unitId, {
        attrs: { ...currentAttrs, activity: selectedActivity }
      })
      onUpdated?.()
      onClose()
    } catch (error) {
      console.error('[ActivityUpdateModal] Error saving activity:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Atualizar Atividade"
      fullScreen={isMobile}
      mobileTopAligned={isMobile}
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading || !selectedActivity}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={t('actions.save')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              aria-label={t('actions.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Selecione o ramo de atividade desta área de trabalho. Isso define as categorias de produtos disponíveis.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-r-transparent" />
          </div>
        ) : (
          <div className="space-y-3">
            {ACTIVITIES.map((activity) => {
              const isSelected = selectedActivity === activity.id
              return (
                <button
                  key={activity.id}
                  onClick={() => setSelectedActivity(activity.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 bg-white dark:bg-slate-800'
                  }`}
                >
                  <span className="text-3xl shrink-0">{activity.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-white mb-1">{activity.name}</p>
                    <div className="flex flex-wrap gap-1">
                      {activity.categories.map((cat) => (
                        <span
                          key={cat}
                          className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}
